<div class="sa-step">
  <div class="step-header-row">
    <h3 class="step-title">Service Addresses</h3>
    <div class="header-actions">
      @if (canUndoMerge()) {
        <button class="undo-btn" (click)="undoMerge()">
          <i class="material-symbols-rounded">undo</i>
          Undo Merge
        </button>
      }
      @if (canUndoDelete()) {
        <button class="undo-btn" (click)="undoDelete()">
          <i class="material-symbols-rounded">undo</i>
          Undo Delete
        </button>
      }
    </div>
  </div>

  <app-parent-context-banner></app-parent-context-banner>
  <app-parent-conflict-resolver></app-parent-conflict-resolver>

  <!-- Duplicate banners: same-LEN (info) + cross-LEN (warning) -->
  @if (hasSameLenDuplicates() || hasCrossLenDuplicates()) {
    <div class="duplicate-banners">
      @if (hasSameLenDuplicates()) {
        <div class="duplicate-banner duplicate-banner--info">
          <div class="duplicate-banner-header">
            <i class="material-symbols-rounded">merge</i>
            <span>Some service addresses will be merged on import</span>
          </div>
          <p class="duplicate-banner-desc">
            The same service address appears more than once. The backend will collapse these into a single record automatically.
          </p>
          @for (group of sameLenDuplicates(); track group.saGroupIds[0]) {
            <div class="duplicate-group-row">
              <i class="material-symbols-rounded">location_on</i>
              <span class="duplicate-addresses">
                @for (saId of group.saGroupIds; track saId; let last = $last) {
                  <strong>{{ saAddressMap[saId] || 'Unknown' }}</strong>@if (!last) {<span>, </span>}
                }
              </span>
            </div>
          }
        </div>
      }
      @if (hasCrossLenDuplicates()) {
        <div class="duplicate-banner duplicate-banner--warning">
          <div class="duplicate-banner-header">
            <i class="material-symbols-rounded">warning</i>
            <span>Some service addresses appear under multiple legal entities</span>
          </div>
          <p class="duplicate-banner-desc">
            Please confirm you didn't accidentally place the same address under two different LENs.
            You can merge the addresses, merge the legal entities, or edit one to resolve.
          </p>
          @for (group of crossLenDuplicates(); track group.saGroupIds[0]) {
            <div class="duplicate-group-row">
              <i class="material-symbols-rounded">location_on</i>
              <span class="duplicate-addresses">
                @for (saId of group.saGroupIds; track saId; let last = $last) {
                  <strong>{{ saAddressMap[saId] || 'Unknown' }}</strong>@if (!last) {<span>, </span>}
                }
              </span>
              <button class="select-merge-btn" (click)="selectDuplicatesForMerge(group.saGroupIds)">
                <i class="material-symbols-rounded">merge</i>
                Select for Merge
              </button>
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- SA List grouped by LEN -->
  <div class="sa-list">
    @for (len of legalEntities(); track len.groupId) {
      <div class="len-group">
        <!-- LEN Group Header -->
        <div class="len-group-header">
          <div class="entity-icon len-icon">
            <i class="material-symbols-rounded">account_balance</i>
          </div>
          <div class="entity-name-col">
            <span class="entity-type-label len-type-label">Legal Entity</span>
            @if (editingLenName === len.groupId) {
              <input
                class="inline-name-input"
                [value]="originalNameMap[len.groupId]"
                (keydown.enter)="saveLenName(len.groupId, $event)"
                (keydown.escape)="editingLenName = null"
                (blur)="saveLenName(len.groupId, $event)"
              />
            } @else {
              <span class="len-group-name editable" (click)="editingLenName = len.groupId">
                {{ originalNameMap[len.groupId] }}
                <i class="material-symbols-rounded edit-name-icon">edit</i>
              </span>
            }
          </div>
          @if (saForcedNewMap[len.groupId]) {
            <span class="status-badge new-badge">
              <i class="material-symbols-rounded">check</i>
              New
            </span>
          }
          <span class="len-group-summary">{{ lenChildCountMap[len.groupId] }}</span>
        </div>
        <!-- SA Cards under this LEN -->
        @for (sa of len.serviceAddresses; track sa.groupId) {
          <div class="sa-card"
            [class.is-new]="saIsNewMap[len.groupId + '__' + sa.groupId]"
            [class.merge-selected]="mergeSelectedMap[sa.groupId]"
            [class.has-duplicate]="saDuplicateKindMap[sa.groupId]"
            [class.needs-attention]="fastTrackBlockingSet().has(sa.groupId)">
            <div class="sa-card-layout">
              <!-- Merge checkbox (only when multiple SAs exist) -->
              @if (totalSaCount() > 1) {
                <mat-checkbox
                  [checked]="mergeSelectedMap[sa.groupId]"
                  (change)="toggleMergeSelection(sa.groupId)"
                  (click)="$event.stopPropagation()"
                  class="merge-checkbox"
                ></mat-checkbox>
              }
              <!-- Property Image (always visible) -->
              <div class="property-image-container">
                @if (imageLoading.has(sa.groupId)) {
                  <div class="property-image-placeholder">
                    <i class="material-symbols-rounded">hourglass_top</i>
                    <span>Loading...</span>
                  </div>
                } @else if (saEditDataMap[sa.groupId]?.verifiedImageUrl) {
                  <img
                    class="property-image clickable"
                    [src]="saEditDataMap[sa.groupId].verifiedImageUrl"
                    alt="Property image"
                    title="Click to enlarge"
                    (click)="openImagePreview(saEditDataMap[sa.groupId].verifiedImageUrl)"
                    (error)="$any($event.target).style.display='none'" />
                } @else {
                  <div class="property-image-placeholder">
                    <i class="material-symbols-rounded">map</i>
                    <span>No image</span>
                  </div>
                }

                <!-- Verification status -->
                @if (saEditDataMap[sa.groupId]?.verified) {
                  <div class="verified-badge">
                    <i class="material-symbols-rounded">verified</i>
                    Verified
                  </div>
                } @else if (saEditDataMap[sa.groupId]?.verifiedImageUrl) {
                  <button class="verify-btn" (click)="verifyAddress(sa.groupId)">
                    <i class="material-symbols-rounded">pin_drop</i>
                    Verify Address
                  </button>
                }
              </div>

              <!-- Card Content -->
              <div class="sa-card-content">
                <!-- SA Header Row -->
                <div class="sa-header">
                  <div class="sa-header-top">
                    <div class="entity-icon sa-icon">
                      <i class="material-symbols-rounded">home_work</i>
                    </div>

                    <!-- Address display / Match dropdown -->
                    <div class="sa-address">
                      <span class="entity-type-label sa-type-label">Service Address</span>
                      <div class="sa-address-body">
                        <div class="sa-address-info">
                          @if (saSelectionMap[sa.groupId] !== null) {
                            <div class="match-dropdown-wrapper">
                              <app-score-select
                                [options]="saScoreOptionsMap[sa.groupId]"
                                [value]="saSelectionMap[sa.groupId]"
                                (valueChange)="onSelectionChange(sa.groupId, $event)">
                              </app-score-select>
                            </div>
                          } @else {
                            <div class="address-lines" [class.has-errors]="hasEntityErrors[sa.groupId]">
                              <div class="address-line-1">
                                <span [class.field-missing]="fieldErrors[sa.groupId]?.['street1']"
                                  (click)="fieldErrors[sa.groupId]?.['street1'] && onMissingFieldClick(sa.groupId, 'street1')"
                                  >{{ saAddressPartsMap[sa.groupId]?.street1 || 'Street address' }}</span>@if (saAddressPartsMap[sa.groupId]?.street2) {<span>, {{ saAddressPartsMap[sa.groupId]?.street2 }}</span>}
                              </div>
                              <div class="address-line-2">
                                <span [class.field-missing]="fieldErrors[sa.groupId]?.['city']"
                                  (click)="fieldErrors[sa.groupId]?.['city'] && onMissingFieldClick(sa.groupId, 'city')"
                                  >{{ saAddressPartsMap[sa.groupId]?.city || 'City' }}</span>,
                                <span [class.field-missing]="fieldErrors[sa.groupId]?.['state']"
                                  (click)="fieldErrors[sa.groupId]?.['state'] && onMissingFieldClick(sa.groupId, 'state')"
                                  >{{ saAddressPartsMap[sa.groupId]?.state || 'State' }}</span>&nbsp;<span [class.field-missing]="fieldErrors[sa.groupId]?.['zip']"
                                  (click)="fieldErrors[sa.groupId]?.['zip'] && onMissingFieldClick(sa.groupId, 'zip')"
                                  >{{ saAddressPartsMap[sa.groupId]?.zip || 'ZIP' }}</span>
                              </div>
                            </div>
                            <button class="icon-btn edit-btn" (click)="toggleExpand(len.groupId, sa.groupId); $event.stopPropagation()" title="Edit address">
                              <i class="material-symbols-rounded">edit</i>
                            </button>
                          }
                        </div>

                        <!-- Match selector -->
                        <app-entity-match-selector
                          [groupId]="sa.groupId"
                          [match]="sa.match"
                          [alternatives]="sa.alternatives"
                          [extractedName]="saAddressMap[sa.groupId]"
                          [forcedNew]="saForcedNewMap[len.groupId]"
                          [forcedNewReason]="'Parent is new'"
                          [blocked]="saBlockedMap[sa.groupId] || false"
                          [blockedReason]="'Exists under a different entity — migrate the import or edit the entry'"
                          [exactLocked]="saExactLockedMap[sa.groupId] || false"
                          [exactLockedReason]="'Address already exists — linked to the existing address'"
                          [showDropdown]="false"
                          (selectionChange)="onSelectionChange(sa.groupId, $event)">
                        </app-entity-match-selector>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <!-- Duplicate warning — full-width banner under the SA card
                 so it doesn't wrap the Create New / Link to Existing
                 controls in the address row. -->
            @if (saBlockedMap[sa.groupId]) {
              <div class="jen-exists-elsewhere">
                <img class="jen-exists-avatar" src="assets/img/jen/jen_zen_with_shadow.svg" alt="JenZen" />
                <div class="jen-exists-bubble jen-exists-bubble--error">
                  This address already exists on this Legal Entity as
                  @if (saExistsElsewhereMap[sa.groupId]) {
                    <span class="where">
                      <i class="material-symbols-rounded">home_work</i>{{ saExistsElsewhereMap[sa.groupId] }}
                    </span>
                  } @else {
                    <span class="where">an address under a different legal entity</span>
                  }.
                  Addresses are unique per customer.
                  <span class="link-hint">
                    Migrate this import up the hierarchy to use the existing address — or if this is genuinely a different location, edit the address to make it distinct.
                  </span>
                </div>
              </div>
            }
            @if (saDuplicateKindMap[sa.groupId]) {
              <div class="sa-duplicate-warning"
                   [class.sa-duplicate-warning--info]="saDuplicateKindMap[sa.groupId] === 'same-len-info'"
                   [class.sa-duplicate-warning--cross]="saDuplicateKindMap[sa.groupId] === 'cross-len-warning'">
                <i class="material-symbols-rounded">{{ saDuplicateKindMap[sa.groupId] === 'same-len-info' ? 'merge' : 'warning' }}</i>
                <span>{{ saDuplicateMessageMap[sa.groupId] }}</span>
              </div>
            }

            @if (expandedSaSet.has(sa.groupId)) {
              <div class="sa-edit-section">
                <div class="edit-panel-header">
                  <i class="material-symbols-rounded">edit</i>
                  <span>Edit Address Details</span>
                </div>

                <div class="edit-fields-grid">
                  <!-- Service Address Name -->
                  <mat-form-field appearance="outline">
                    <mat-label>Service Address Name (optional)</mat-label>
                    <input matInput [ngModel]="saEditDataMap[sa.groupId]?.label || ''"
                      (ngModelChange)="onSaFieldChange(sa.groupId, 'label', $event)"
                      placeholder="e.g. property name" />
                  </mat-form-field>

                  <!-- Street 1 -->
                  <mat-form-field appearance="outline"
                    [class.field-error]="fieldErrors[sa.groupId]?.['street1']">
                    <mat-label>Street Address</mat-label>
                    <input matInput [id]="'sa-' + sa.groupId + '-street1'"
                      [ngModel]="saEditDataMap[sa.groupId].street1 || ''"
                      (ngModelChange)="onSaFieldChange(sa.groupId, 'street1', $event)" />
                  </mat-form-field>

                  <!-- Street 2 -->
                  <mat-form-field appearance="outline">
                    <mat-label>Address 2 (Suite, Floor, Building)</mat-label>
                    <input matInput [ngModel]="saEditDataMap[sa.groupId].street2 || ''"
                      (ngModelChange)="onSaFieldChange(sa.groupId, 'street2', $event)" />
                  </mat-form-field>

                  <!-- City, State, ZIP on same row -->
                  <div class="address-row">
                    <mat-form-field appearance="outline" class="city-field"
                      [class.field-error]="fieldErrors[sa.groupId]?.['city']">
                      <mat-label>City</mat-label>
                      <input matInput [id]="'sa-' + sa.groupId + '-city'"
                        [ngModel]="saEditDataMap[sa.groupId].city || ''"
                        (ngModelChange)="onSaFieldChange(sa.groupId, 'city', $event)" />
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="state-field"
                      [class.field-error]="fieldErrors[sa.groupId]?.['state']">
                      <mat-label>State</mat-label>
                      <mat-select [id]="'sa-' + sa.groupId + '-state'"
                        [ngModel]="saEditDataMap[sa.groupId].state || ''"
                        (ngModelChange)="onSaFieldChange(sa.groupId, 'state', $event)">
                        @for (s of stateOptions; track s) {
                          <mat-option [value]="s">{{ s }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="zip-field"
                      [class.field-error]="fieldErrors[sa.groupId]?.['zip']">
                      <mat-label>ZIP Code</mat-label>
                      <input matInput [id]="'sa-' + sa.groupId + '-zip'"
                        [ngModel]="saEditDataMap[sa.groupId].zip || ''"
                        (ngModelChange)="onSaFieldChange(sa.groupId, 'zip', $event)"
                        maxlength="10" />
                    </mat-form-field>
                  </div>
                </div>
              </div>
            }

            <div class="sa-children">
              @for (meter of sa.meters; track meter.groupId) {
                <div class="child-row meter-row">
                  <div class="entity-icon meter-icon">
                    <i class="material-symbols-rounded">speed</i>
                  </div>
                  <div class="child-info">
                    <span class="child-type">Meter</span>
                    <span class="child-name">{{ meterUanDisplayMap[meter.groupId] }}</span>
                    @if (meter.utility) {
                      <span class="utility-badge">{{ meter.utility.utilityName }}</span>
                    }
                  </div>
                  <button class="stat-badge bill-stat" (click)="previewBill(meter)" title="View Bills">
                    <i class="material-symbols-rounded">receipt_long</i>
                    {{ meter.bills.length }} bill{{ meter.bills.length !== 1 ? 's' : '' }}
                    <i class="material-symbols-rounded view-arrow">
                      {{ meter.bills.length > 1 && expandedBillMeters[meter.groupId] ? 'expand_less' : 'arrow_forward' }}
                    </i>
                  </button>
                </div>
                @if (expandedBillMeters[meter.groupId] && meter.bills.length > 1) {
                  <div class="expanded-bills">
                    @for (bill of meter.bills; track bill.groupId) {
                      <div class="bill-row clickable"
                           [class.previewing]="previewingBillMap[bill.groupId]"
                           (click)="previewSpecificBill(bill)">
                        <i class="material-symbols-rounded bill-icon">receipt_long</i>
                        <span class="bill-dates">{{ billLabelMap[bill.groupId] }}</span>
                        <i class="material-symbols-rounded view-icon">visibility</i>
                      </div>
                    }
                  </div>
                }
              }
            </div>
          </div>
        }
      </div>
    }
  </div>

  <!-- Address Verification Modal -->
  @if (showVerificationModal()) {
    <div class="verification-overlay" (click)="cancelVerification()">
      <div class="verification-modal" (click)="$event.stopPropagation()">
        <div class="verification-header">
          <i class="material-symbols-rounded">pin_drop</i>
          <h4>Verify Address</h4>
          <button class="close-btn" (click)="cancelVerification()">
            <i class="material-symbols-rounded">close</i>
          </button>
        </div>
        <p class="verification-description">
          We found a suggested address. Which would you like to use?
        </p>

        <div class="verification-options">
          <!-- Extracted Address -->
          <div class="address-option">
            <span class="option-label">Extracted from Bill</span>
            <div class="option-address">
              {{ saEditDataMap[showVerificationModal()!].street1 }}<br>
              @if (saEditDataMap[showVerificationModal()!].street2) {
                {{ saEditDataMap[showVerificationModal()!].street2 }}<br>
              }
              {{ saEditDataMap[showVerificationModal()!].city }},
              {{ saEditDataMap[showVerificationModal()!].state }}
              {{ saEditDataMap[showVerificationModal()!].zip }}
            </div>
            <button class="btn-secondary" (click)="confirmAddress(showVerificationModal()!, 'extracted')">
              Keep Extracted
            </button>
          </div>

          <!-- Suggested Address -->
          @if (suggestedAddresses.get(showVerificationModal()!)) {
            <div class="address-option suggested">
              <span class="option-label">
                <i class="material-symbols-rounded">auto_fix_high</i>
                Google Suggested
              </span>
              <div class="option-address">
                {{ suggestedAddresses.get(showVerificationModal()!).street }}<br>
                {{ suggestedAddresses.get(showVerificationModal()!).city }},
                {{ suggestedAddresses.get(showVerificationModal()!).state }}
                {{ suggestedAddresses.get(showVerificationModal()!).zip }}
              </div>
              <button class="btn-primary" (click)="confirmAddress(showVerificationModal()!, 'suggested')">
                Use Suggested
              </button>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Image Preview Overlay -->
  @if (previewImageUrl()) {
    <div class="image-preview-overlay" (click)="closeImagePreview()">
      <div class="image-preview-container" (click)="$event.stopPropagation()">
        <button class="close-btn" (click)="closeImagePreview()">
          <i class="material-symbols-rounded">close</i>
        </button>
        <img [src]="previewImageUrl()" alt="Property image (enlarged)" class="preview-image" />
      </div>
    </div>
  }

  <!-- Floating action bar (merge button — matches dashboard hierarchy UX) -->
  <app-selection-action-bar
    [selectedAddressCount]="mergeSelection().length"
    [isHierarchyView]="true"
    (mergeAddresses)="openMergeModal()"
    (deleteAddresses)="deleteSelected()">
  </app-selection-action-bar>

  <!-- Merge modal (shared with dashboard) -->
  <app-merge-modal
    [subLabelMap]="mergeModalState.subLabelMap"
    [isOpen]="mergeModalState.isOpen"
    [type]="'address'"
    [candidates]="mergeModalState.candidates"
    [labelMap]="mergeModalState.labelMap"
    (closeModal)="closeMergeModal()"
    (confirm)="onMergeConfirmed($event)">
  </app-merge-modal>

  @if (saMergePromptPair(); as mergePair) {
    <div class="last-item-confirm-overlay" (click)="dismissAutoMerge()">
      <div class="last-item-confirm-dialog merge-prompt-dialog" (click)="$event.stopPropagation()">
        <div class="confirm-icon merge-prompt-icon">
          <i class="material-symbols-rounded">merge</i>
        </div>
        <h4>We found a matching address</h4>
        <p><strong>{{ mergePair.addressLabel }}</strong></p>
        <p>Want us to combine these into one service address?</p>
        <div class="confirm-actions">
          <button class="btn-secondary" (click)="dismissAutoMerge()">Keep separate</button>
          <button class="btn-primary merge-prompt-confirm" (click)="confirmAutoMerge()">
            <i class="material-symbols-rounded">merge</i>
            Merge
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Last-item delete confirmation -->
  @if (showLastItemConfirm) {
    <div class="last-item-confirm-overlay" (click)="cancelLastItemDelete()">
      <div class="last-item-confirm-dialog" (click)="$event.stopPropagation()">
        <div class="confirm-icon">
          <i class="material-symbols-rounded">warning</i>
        </div>
        <h4>Remove all service addresses?</h4>
        <p>
          You're about to remove the last service address from this import.
          This will end the current session and return you to the upload screen.
        </p>
        <div class="confirm-actions">
          <button class="btn-secondary" (click)="cancelLastItemDelete()">Cancel</button>
          <button class="btn-danger" (click)="confirmLastItemDelete()">
            <i class="material-symbols-rounded">delete</i>
            Remove &amp; Exit
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Validation Banner -->
  @if (validationSummary.totalErrors > 0) {
    <div class="validation-banner">
      <div class="validation-banner-title">
        <i class="material-symbols-rounded">block</i>
        Fix these before continuing
      </div>
      @for (group of validationSummary.errorGroups; track group.entityLabel) {
        <div class="validation-banner-group">
          <div class="group-label">{{ group.entityLabel }}</div>
          <ul>
            @for (field of group.fields; track field) {
              <li>{{ field }}</li>
            }
          </ul>
        </div>
      }
    </div>
  }

  <!-- Actions -->
  <div class="actions-bar">
    <button class="btn-secondary" (click)="goBack()">
      <i class="material-symbols-rounded">arrow_back</i>
      Back
    </button>
    <button class="btn-primary" [disabled]="hasSaStateMismatch() || anySaBlocked" (click)="goForward()">
      Continue
      <i class="material-symbols-rounded">arrow_forward</i>
    </button>
  </div>
</div>
